import { ReactElement, useMemo } from 'react'
import { CubicBezierCurve3, LineCurve3, ColorRepresentation } from 'three'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import BoxMesh from '../../BoxMesh'
import { CONNECTOR_BOX_HEIGHT, CONNECTOR_BOX_OFFSET } from '../../BoxMesh/constants'
import { ConnectorKind } from '../../BoxMesh/utils'
import SimpleCylinderMesh from '../../SimpleCylinderMesh'
import { createMeshStandardMaterialMemoized, getPointAtPercentage } from '../../utils'

interface Props {
  onClick?: (event: ThreeEvent<MouseEvent>) => void
  position: ImmutableVector3
  domain?: Domain
  relativePosition?: number
  isActive?: boolean
  color?: string | ColorRepresentation
  isTarget?: boolean
  connectorKind?: ConnectorKind
  supportPosition?: ImmutableVector3
  disableDepthTest?: boolean
  visible?: boolean
}

const CylinderMesh = ({
  onClick,
  position,
  domain,
  relativePosition,
  isActive = false,
  color = 'green',
  supportPosition,
  isTarget = false,
  connectorKind = 'tensile',
  disableDepthTest,
  visible = true,
}: Props): ReactElement => {
  const height = CONNECTOR_BOX_HEIGHT
  const offset = CONNECTOR_BOX_OFFSET
  const controlOffset = offset * 2
  const curves = useMemo(() => {
    if (!supportPosition) return null

    const start = supportPosition.add(new ImmutableVector3(0, 0, offset))
    const end = position.sub(new ImmutableVector3(0, 0, offset))
    const control1 = getPointAtPercentage(start, end, 0.1).sub(
      new ImmutableVector3(0, 0, controlOffset),
    )
    const control2 = getPointAtPercentage(start, end, 0.9).add(
      new ImmutableVector3(0, 0, controlOffset),
    )

    const connectorLine = new CubicBezierCurve3(start.v, control1.v, control2.v, end.v)
    const supportLine = new LineCurve3(start.v, start.add(new ImmutableVector3(0, 0, height)).v)
    const targetLine = new LineCurve3(end.v, end.sub(new ImmutableVector3(0, 0, height)).v)

    return [connectorLine, supportLine, targetLine]
  }, [controlOffset, height, offset, position, supportPosition])

  const curveMaterial = createMeshStandardMaterialMemoized({
    color: 'black',
    polygonOffset: true,
    polygonOffsetUnits: -250,
    depthTest: disableDepthTest ? false : undefined,
  })

  const connectorBody =
    domain !== undefined && relativePosition !== undefined ? (
      <BoxMesh
        position={position}
        domain={domain}
        relativePosition={relativePosition}
        connectorKind={connectorKind}
        isTarget={isTarget}
        color={color}
        onClick={onClick}
        isActive={isActive}
        height={height}
        disableDepthTest={disableDepthTest}
        maxScale={2.5}
        visible={visible}
      />
    ) : (
      <SimpleCylinderMesh
        position={
          isTarget
            ? position.sub(new ImmutableVector3(0, 0, height / 2 + offset))
            : position.add(new ImmutableVector3(0, 0, height / 2 + offset))
        }
        color={color}
        onClick={onClick}
        isActive={isActive}
        height={height}
        disableDepthTest={disableDepthTest}
        maxScale={2.5}
        visible={visible}
      />
    )

  return (
    <>
      {isActive && curves && (
        <>
          {curves.map((curve, index) => (
            <mesh key={index} material={curveMaterial} visible={visible}>
              <tubeGeometry args={[curve, 100, 0.006, 50, false]} />
            </mesh>
          ))}
        </>
      )}
      {connectorBody}
    </>
  )
}

export default CylinderMesh
